import { useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated } from '@/core/auth/SessionStore'
import { cn } from '@/lib/cn'
import {
  errosDaFicha,
  totalProducao,
  totalValor,
  totalVolume,
  useCarreteiroStore,
  viagemProducao,
} from '@/features/carreteiro/carreteiroStore'
import { dataBR } from '@/features/carreteiro/carreteiroRegras'
import { saveCarreteiroFicha } from '@/features/carreteiro/carreteiroRepo'
import { deleteDraft } from '@/features/carreteiro/draftCarreteiroRepo'
import { usePrecheckCarreteiro } from '@/features/carreteiro/useCabecalhoCarreteiro'

/**
 * Step 2: revisão e salvar.
 *  - Todas as viagens passam pelas regras do SIGA; com erro, lista por viagem
 *    e não salva.
 *  - Online: repete o precheck do cabeçalho (medição, transferência,
 *    retroativo, vigência) — a situação pode ter mudado desde o passo 1.
 *  - Salvar grava no outbox e tenta enviar na hora.
 */
export function CarreteiroStep2Route() {
  const authed = useIsAuthenticated()
  const navigate = useNavigate()
  const store = useCarreteiroStore()
  const precheck = usePrecheckCarreteiro(store.obraId, store.mveId, store.dt)

  const [saving, setSaving] = useState(false)
  // Depois de salvar, o store é zerado ANTES de a navegação para /success
  // comitar (zustand re-renderiza síncrono; o navigate do react-router entra
  // como transição). Sem esta trava, o guard abaixo via o store vazio e mandava
  // o operador de volta ao início — parecia que o lançamento não tinha salvo.
  const salvouRef = useRef(false)
  const [error, setError] = useState<string | null>(null)

  if (!authed) return <Navigate to="/login" replace />
  if (salvouRef.current) return null
  if (store.viagens.length === 0) return <Navigate to="/carreteiro/step1" replace />

  const errosFicha = errosDaFicha(store)
  const bloqueado = precheck.status === 'bloqueado'
  const valor = totalValor(store)

  async function handleSave() {
    if (errosFicha.length > 0 || bloqueado) return
    setSaving(true)
    setError(null)
    try {
      const { pushResult } = await saveCarreteiroFicha(store)
      const viagensCount = store.viagens.length
      const fichaId = store.id
      // Ficha foi pra outbox — limpa draft pra não aparecer mais na index.
      await deleteDraft(fichaId)
      salvouRef.current = true
      navigate('/carreteiro/success', {
        replace: true,
        state: { viagensCount, pushResult },
      })
      store.reset()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const fmt = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/carreteiro/step1')}
            aria-label="Voltar"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold text-on-surface">Carreteiro · 3 de 3 · Revisar</h1>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-2xl mx-auto w-full">
        {/* Bloqueios e erros primeiro — são o motivo de não poder salvar */}
        {precheck.status === 'loading' && (
          <p className="text-xs text-on-surface-dim inline-flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Conferindo regras do SIGA…
          </p>
        )}
        {precheck.bloqueios.length > 0 && (
          <Alerta tone="bad" titulo="O SIGA não aceita esta ficha">
            {precheck.bloqueios.map((b) => (
              <li key={b.code}>{b.message}</li>
            ))}
          </Alerta>
        )}
        {errosFicha.length > 0 && (
          <Alerta tone="bad" titulo="Corrija antes de salvar">
            {errosFicha.flatMap((f) =>
              f.erros.map((e) => (
                <li key={`${f.viagemId}-${e.campo}-${e.mensagem}`}>
                  Viagem {f.indice + 1}: {e.mensagem}
                </li>
              )),
            )}
          </Alerta>
        )}
        {precheck.avisos.length > 0 && (
          <Alerta tone="warn" titulo="Avisos">
            {precheck.avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </Alerta>
        )}
        {precheck.message && <p className="text-xs text-warn">{precheck.message}</p>}

        {/* Stats */}
        <div className="card-surface-variant px-4 py-4 grid grid-cols-2 gap-3 text-center">
          <Stat label="Viagens" value={String(store.viagens.length)} />
          <Stat label="Quantidade" value={fmt(totalVolume(store))} />
          <Stat label="Produção" value={fmt(totalProducao(store))} />
          <Stat
            label="Valor estimado"
            value={valor == null ? '—' : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          />
        </div>

        {/* Ficha resumo */}
        <div className="card-surface p-4 space-y-2">
          <h2 className="text-xs font-bold text-accent uppercase">Ficha</h2>
          <Row label="Tipo" value={store.tipo === 'R' ? 'Rodoviário' : 'Urbana'} />
          <Row label="Data" value={dataBR(store.dt)} />
          <Row label="Obra" value={store.obraNome} />
          <Row
            label="Chefe de equipe"
            value={precheck.status === 'ok' || precheck.status === 'bloqueado'
              ? '(conferido pelo SIGA)'
              : store.chefeEquipeNome ?? 'definido no envio'}
          />
          <Row
            label="Caminhão"
            value={
              store.mvePrefixo
                ? store.mvePlaca
                  ? `${store.mvePrefixo} · ${store.mvePlaca}`
                  : store.mvePrefixo
                : '—'
            }
          />
          {store.mveProprio ? (
            <>
              <Row label="Frota" value="Própria — entra na parte diária do dia" />
              <Row label="Nº da ficha" value="Gerado pelo servidor" />
            </>
          ) : (
            store.tipo === 'R' && (
              <Row label="KM inicial → final" value={`${store.kmInicial ?? '—'} → ${store.kmFinal ?? '—'}`} />
            )
          )}
        </div>

        {/* Padrão do dia (resumo do que está congelado pra todas as viagens) */}
        <div className="card-surface p-4 space-y-2">
          <h2 className="text-xs font-bold text-accent uppercase">Padrão do dia</h2>
          <Row label="Serviço" value={store.defaults.servico?.nome ?? '—'} />
          {store.defaults.servico?.materialNome && (
            <Row
              label="Material"
              value={`${store.defaults.servico.materialNome}${store.defaults.servico.unidadeNome ? ` (${store.defaults.servico.unidadeNome})` : ''}`}
            />
          )}
          <Row label="Origem" value={store.defaults.localCargaNome ?? '—'} />
          <Row label="Destino padrão" value={store.defaults.localDescargaNome ?? '—'} />
          {store.defaults.cargaDtFixo != null && (
            <Row label="DT fixo (km)" value={store.defaults.cargaDtFixo.toString()} />
          )}
          {store.tipo === 'R' && store.defaults.cargaEstacaOrigem && (
            <Row label="Estaca origem" value={store.defaults.cargaEstacaOrigem} />
          )}
          <Row label="Com NF" value={store.fichaConcreto === 'S' ? 'Sim (todas as viagens)' : 'Não'} />
        </div>

        {/* Viagens mini */}
        <div>
          <h2 className="text-xs font-bold text-accent uppercase px-1 mb-2">Viagens</h2>
          <ul className="space-y-2">
            {store.viagens.map((v) => {
              const temErro = errosFicha.some((f) => f.viagemId === v.id)
              return (
                <li
                  key={v.id}
                  className={cn(
                    'card-surface-variant px-4 py-3 flex items-center gap-3',
                    temErro && 'border border-bad/70',
                  )}
                >
                  {temErro ? (
                    <AlertTriangle className="h-5 w-5 text-bad flex-shrink-0" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-ok flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-on-surface text-sm truncate">
                      {v.cargaHora ?? '—'} → {v.descargaHora ?? '—'}
                      {store.tipo === 'R' && v.descargaEstacaDescarga ? ` · Est. ${v.descargaEstacaDescarga}` : ''}
                    </p>
                    <p className="text-xs text-on-surface-dim mt-0.5">
                      {v.volume != null ? `${v.volume} m³ · ` : ''}
                      Prod {fmt(viagemProducao(store, v))}
                      {v.nf ? ` · NF ${v.nf}` : ''}
                    </p>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>

        {error && <p className="text-sm text-bad text-center">{error}</p>}
      </div>

      <div className="sticky bottom-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe">
        <Button
          variant="primary"
          size="lg"
          disabled={saving || errosFicha.length > 0 || bloqueado}
          onClick={handleSave}
          className="w-full"
        >
          {saving ? 'Salvando…' : `Salvar ficha (${store.viagens.length} viagens)`}
        </Button>
      </div>
    </main>
  )
}

function Alerta({
  tone,
  titulo,
  children,
}: {
  tone: 'bad' | 'warn'
  titulo: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-3',
        tone === 'bad' ? 'border-bad bg-bad/10' : 'border-warn bg-warn/10',
      )}
    >
      <p className={cn('text-sm font-medium', tone === 'bad' ? 'text-bad' : 'text-warn')}>{titulo}</p>
      <ul className="mt-1 space-y-0.5 text-sm text-on-surface list-disc pl-5">{children}</ul>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-on-surface-dim uppercase">{label}</p>
      <p className="text-2xl font-black text-accent mt-1">{value}</p>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-on-surface-dim">{label}</span>
      <span className="text-on-surface text-right">{value || '—'}</span>
    </div>
  )
}
